// AdminV3Funnel — F9.3 · WAVE 3 pre-cutover monitoring dashboard.
//
// Real-time funnel snapshot for Landing V3: page_view → CTA clicks + a
// pricing plan breakdown. Auto-refresh every 30s during the monitoring
// window. Palette V10 (bg #0A0A0A, card #111111, accent #FFFFFF).
//
// Route: /admin/analytics/v3-funnel  (admin-only, ProtectedRoute gated)
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft, Eye, MousePointerClick, Zap, Rocket, TrendingUp,
    RefreshCw, Clock,
} from "lucide-react";
import { apiFetch } from "@/lib/api";
import { toast } from "sonner";

const WINDOWS = [
    { key: "1h",  label: "1 ora"     },
    { key: "24h", label: "24 ore"    },
    { key: "7d",  label: "7 giorni"  },
];

const PLAN_ROWS = ["starter", "pro", "business", "enterprise"];
const PLAN_COLOR = {
    starter:    "#CCCCCC",
    pro:        "#FFFFFF",
    business:   "#CCCCCC",
    enterprise: "#A3A3A3",
};

const REFRESH_MS = 30_000;


function MetricCard({ testId, icon: Icon, label, value, delta, delay = 0 }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay }}
            data-testid={testId}
            className="rounded-2xl border p-5"
            style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
        >
            <div className="flex items-start justify-between">
                <span
                    className="h-9 w-9 rounded-xl grid place-items-center"
                    style={{ backgroundColor: "rgba(255, 255, 255,0.12)" }}
                >
                    <Icon className="h-4 w-4" style={{ color: "#FFFFFF" }} aria-hidden />
                </span>
            </div>
            <p className="mt-4 text-3xl font-semibold tabular-nums tracking-tight text-white">
                {value}
            </p>
            <p className="mt-1 text-xs text-[#CCCCCC]">{label}</p>
            {delta && <p className="mt-2 text-[11px] text-[#999999]">{delta}</p>}
        </motion.div>
    );
}


export default function AdminV3Funnel() {
    const [window, setWindow] = useState("24h");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [lastFetch, setLastFetch] = useState(null);

    const load = useCallback(async () => {
        setRefreshing(true);
        try {
            const res = await apiFetch(`/admin/analytics/v3-funnel?window=${window}`);
            setData(res);
            setLastFetch(new Date());
        } catch (e) {
            toast.error(e.message || "Impossibile caricare il funnel");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [window]);

    useEffect(() => {
        setLoading(true);
        load();
    }, [load]);

    useEffect(() => {
        const t = setInterval(load, REFRESH_MS);
        return () => clearInterval(t);
    }, [load]);

    // Pin body bg dark for admin surface.
    useEffect(() => {
        const prev = document.body.style.backgroundColor;
        document.body.style.backgroundColor = "#0A0A0A";
        return () => { document.body.style.backgroundColor = prev; };
    }, []);

    const empty = data && (data.counts?.page_view || 0) === 0
        && (data.counts?.hero_cta_click || 0) === 0
        && (data.counts?.pricing_cta_click || 0) === 0
        && (data.counts?.final_cta_click || 0) === 0
        && (data.counts?.header_anon_cta_click || 0) === 0;

    return (
        <div
            data-testid="admin-v3-funnel-root"
            className="min-h-screen text-white"
            style={{ backgroundColor: "#0A0A0A" }}
        >
            <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
                <div className="flex items-center gap-3 text-[#CCCCCC] text-sm">
                    <Link
                        to="/admin"
                        className="inline-flex items-center gap-1 hover:text-white transition-colors no-underline"
                    >
                        <ArrowLeft className="w-4 h-4" /> Admin
                    </Link>
                    <span>/</span>
                    <span>Analytics</span>
                    <span>/</span>
                    <span className="text-white">V3 Funnel</span>
                </div>
                <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
                            Landing V3 · Funnel di conversione
                        </h1>
                        <p className="mt-1 text-sm text-[#CCCCCC]">
                            Baseline click e CTR sui CTA della nuova landing (WAVE 3 pre-cutover). Auto-refresh 30s.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* window switcher */}
                        <div
                            className="inline-flex rounded-xl p-1 border"
                            style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                        >
                            {WINDOWS.map((w) => (
                                <button
                                    key={w.key}
                                    type="button"
                                    onClick={() => setWindow(w.key)}
                                    data-testid={`admin-v3-funnel-window-${w.key}`}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                        window === w.key
                                            ? "text-white"
                                            : "text-[#CCCCCC] hover:text-white"
                                    }`}
                                    style={{
                                        backgroundColor: window === w.key ? "#FFFFFF" : "transparent",
                                    }}
                                >
                                    {w.label}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={load}
                            disabled={refreshing}
                            data-testid="admin-v3-funnel-refresh"
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium border text-white/90 hover:text-white transition-colors disabled:opacity-50"
                            style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                            {refreshing ? "Refresh…" : "Refresh"}
                        </button>
                    </div>
                </div>
                {lastFetch && (
                    <p className="mt-2 text-[11px] text-[#999999] inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Ultimo aggiornamento {lastFetch.toLocaleTimeString("it-IT")}
                    </p>
                )}
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {loading && (
                    <div className="rounded-2xl border p-10 text-center text-sm text-[#CCCCCC]"
                        style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}>
                        Caricamento funnel…
                    </div>
                )}

                {!loading && empty && (
                    <div
                        data-testid="admin-v3-funnel-empty"
                        className="rounded-2xl border p-10 text-center"
                        style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                    >
                        <p className="text-sm text-[#FFFFFF]">Nessun dato ancora — attendi il primo click.</p>
                        <p className="mt-2 text-xs text-[#CCCCCC]">
                            Il funnel si popola man mano che gli utenti visitano <span className="font-mono text-white">/</span> e cliccano i CTA V3.
                        </p>
                    </div>
                )}

                {!loading && !empty && data && (
                    <>
                        {/* Metrics row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <MetricCard
                                testId="admin-v3-funnel-metric-page_view"
                                icon={Eye}
                                label="Page views V3"
                                value={data.counts.page_view.toLocaleString("it-IT")}
                                delay={0}
                            />
                            <MetricCard
                                testId="admin-v3-funnel-metric-hero"
                                icon={MousePointerClick}
                                label="Hero CTA click"
                                value={data.counts.hero_cta_click.toLocaleString("it-IT")}
                                delta={`CTR ${data.conversion.hero_ctr_pct}%`}
                                delay={0.05}
                            />
                            <MetricCard
                                testId="admin-v3-funnel-metric-pricing"
                                icon={Zap}
                                label="Pricing CTA click"
                                value={data.counts.pricing_cta_click.toLocaleString("it-IT")}
                                delta={`CTR ${data.conversion.pricing_ctr_pct}%`}
                                delay={0.1}
                            />
                            <MetricCard
                                testId="admin-v3-funnel-metric-final"
                                icon={Rocket}
                                label="Final CTA click"
                                value={data.counts.final_cta_click.toLocaleString("it-IT")}
                                delta={`CTR ${data.conversion.final_ctr_pct}%`}
                                delay={0.15}
                            />
                        </div>

                        {/* Aggregate CTR + Header anon */}
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.2 }}
                                className="rounded-2xl border p-5"
                                style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                            >
                                <div className="flex items-center gap-2 text-xs text-[#CCCCCC]">
                                    <TrendingUp className="w-3.5 h-3.5" style={{ color: "#FFFFFF" }} />
                                    Any-CTA CTR
                                </div>
                                <p className="mt-3 text-3xl font-semibold tabular-nums tracking-tight text-white">
                                    {data.conversion.any_cta_ctr_pct}
                                    <span className="text-lg font-normal text-[#CCCCCC] ml-1">%</span>
                                </p>
                                <p className="mt-1 text-xs text-[#999999]">
                                    Frazione di visitors che ha cliccato almeno un CTA V3 in questa finestra.
                                </p>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.25 }}
                                className="rounded-2xl border p-5"
                                style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                            >
                                <div className="flex items-center gap-2 text-xs text-[#CCCCCC]">
                                    <MousePointerClick className="w-3.5 h-3.5" style={{ color: "#FFFFFF" }} />
                                    Header anon CTA (Prova Ora + Accedi)
                                </div>
                                <p className="mt-3 text-3xl font-semibold tabular-nums tracking-tight text-white">
                                    {data.counts.header_anon_cta_click.toLocaleString("it-IT")}
                                </p>
                                <p className="mt-1 text-xs text-[#999999]">
                                    Click su CTA del Header V3 da utenti non loggati.
                                </p>
                            </motion.div>
                        </div>

                        {/* Pricing breakdown */}
                        <motion.section
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.3 }}
                            className="mt-8 rounded-2xl border p-6"
                            style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                        >
                            <h2 className="text-sm font-semibold text-white">
                                Pricing CTA — breakdown per piano
                            </h2>
                            <p className="mt-1 text-xs text-[#CCCCCC]">
                                Distribuzione dei click sul CTA di ogni tier (`plan` in props).
                            </p>
                            <div className="mt-4 space-y-2">
                                {PLAN_ROWS.map((plan) => {
                                    const n = data.pricing_breakdown_by_plan[plan] || 0;
                                    const total = Math.max(
                                        1,
                                        Object.values(data.pricing_breakdown_by_plan).reduce((a, b) => a + b, 0)
                                    );
                                    const pct = (n / total) * 100;
                                    return (
                                        <div
                                            key={plan}
                                            data-testid={`admin-v3-funnel-pricing-plan-${plan}`}
                                            className="grid grid-cols-[7rem_1fr_4rem_4rem] items-center gap-3"
                                        >
                                            <span className="text-xs font-semibold uppercase tracking-widest text-[#FFFFFF]">
                                                {plan}
                                            </span>
                                            <div
                                                className="relative h-2 rounded-full overflow-hidden"
                                                style={{ backgroundColor: "#0A0A0A" }}
                                            >
                                                <div
                                                    className="h-full rounded-full transition-all"
                                                    style={{
                                                        width: `${pct}%`,
                                                        backgroundColor: PLAN_COLOR[plan],
                                                    }}
                                                />
                                            </div>
                                            <span className="text-right text-sm tabular-nums text-white">
                                                {n.toLocaleString("it-IT")}
                                            </span>
                                            <span className="text-right text-xs tabular-nums text-[#CCCCCC]">
                                                {pct.toFixed(1)}%
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </motion.section>

                        {/* Meta */}
                        <p className="mt-6 text-[11px] text-[#999999]">
                            Finestra <span className="text-white font-semibold">{data.window}</span> ·
                            iniziata {new Date(data.started_at).toLocaleString("it-IT")} ·
                            generato {new Date(data.generated_at).toLocaleString("it-IT")}
                        </p>
                    </>
                )}
            </main>
        </div>
    );
}
